import { Fragment } from 'react';
import { Modal } from 'lib/mortar/components/Modal';

import type { AnsweredQuestion } from './types';

import FreeText from './screens/FreeText';
import RAQOptions from './screens/RAQOptions';

import Header from './components/Header';
import {
  useJobRefinementState,
  ADD_ANSWER,
  SKIP_ANSWER,
  GO_BACK,
  FORM_COMPLETED,
  DISMISS,
  HIDE_RAQ_OPTIONS,
} from './JobRefinement.reducer';

import { screens } from './config';
import {
  JOB_REFINEMENT_INTERACTIONS,
  trackJobRefinementForm,
} from './JobRefinementTracking';
import HeaderEditMode from './components/HeaderEditMode';
import { FeedbackModal } from 'components/Funnelling/components/FeedbackModal';
import { useDisplayFeedbackModal } from '../../@hooks/use-display-feedback-modal';

const JobRefinementModal = ({
  disableDismiss,
  enableRAQOption,
  isInRaqEntryPointMode,
  onExitModal,
}: {
  disableDismiss?: boolean;
  enableRAQOption?: boolean;
  isInRaqEntryPointMode?: boolean;
  onExitModal?: () => void;
}) => {
  const { state, dispatch } = useJobRefinementState();

  const { displayFeedbackModal, feedbackCallback, exitWithFeedbackCheck } =
    useDisplayFeedbackModal();

  const {
    form,
    showJobRefinementModal,
    questionIndex,
    answeredQuestions,
    editMode,
    showRaqOptions,
    origin,
  } = state;

  const getAnswers = (payload: AnsweredQuestion) => {
    let is_other = null;
    if (!payload.answers) return {};
    if (payload.answers.length > 1) {
      const answers = payload.answers.map(answer => {
        if (answer?.key === 'other') is_other = true;

        if (answer.id) return answer.id;

        return answer.value;
      });
      return { answers: answers, is_other };
    }

    return {
      answer: payload.answers[0]?.id
        ? payload.answers[0]?.id
        : payload.answers[0]?.value,
      is_other: payload.answers[0]?.key === 'other' ? true : null,
    };
  };

  const questionCount = form.questions?.length || 0;
  const maxSteps = questionCount + Number(!!enableRAQOption);

  const onNext = (payload: AnsweredQuestion) => {
    if (editMode) {
      dispatch({ type: FORM_COMPLETED, isInRaqEntryPointMode });
    } else if (questionCount === questionIndex + 1) {
      dispatch({
        type: FORM_COMPLETED,
        showRaqOptions: !!enableRAQOption && origin === 'pre-select',
        isInRaqEntryPointMode,
      });
    }

    dispatch({ type: ADD_ANSWER, payload });
    trackJobRefinementForm(
      JOB_REFINEMENT_INTERACTIONS.VIEW,
      state,
      getAnswers(payload),
    );
  };

  const onSkip = () => {
    if (questionCount === questionIndex + 1 || editMode) {
      dispatch({ type: FORM_COMPLETED, isInRaqEntryPointMode });
    }

    dispatch({ type: SKIP_ANSWER });
    trackJobRefinementForm(JOB_REFINEMENT_INTERACTIONS.SKIP, state);
  };

  const onBack = () => {
    if (showRaqOptions) return dispatch({ type: HIDE_RAQ_OPTIONS });
    if (questionIndex - 1 < 0 || editMode) {
      exitWithFeedbackCheck(() => {
        if (!disableDismiss) {
          dispatch({ type: DISMISS });
          trackJobRefinementForm(JOB_REFINEMENT_INTERACTIONS.DISCARD, state);
        } else {
          onExitModal?.();
        }
      });
    } else {
      dispatch({ type: GO_BACK });
      trackJobRefinementForm(JOB_REFINEMENT_INTERACTIONS.BACK, state);
    }
  };

  const onDismiss = () => {
    dispatch({ type: DISMISS });
    trackJobRefinementForm(JOB_REFINEMENT_INTERACTIONS.DISCARD, state);
  };

  const question = form.questions?.[questionIndex];
  if (!showJobRefinementModal || (!question && !showRaqOptions)) return null;

  const savedAnswer = question
    ? answeredQuestions.get(question.questionId)
    : undefined;

  const Component = (question && screens[question.format]) || FreeText;

  if (feedbackCallback && displayFeedbackModal) {
    return <FeedbackModal stopAndClose={feedbackCallback} />;
  }

  return (
    <Modal
      size="medium"
      className="!min-h-[75vh] px-4 py-6 sm:!max-h-[80vh] sm:px-8"
      fullScreenOnMobile
      name="Job Refinement"
      noPadding
      hideCloseButton
      onDismiss={() => exitWithFeedbackCheck(onDismiss)}
      closeOnClickOutside={!disableDismiss}
    >
      <Fragment key={question?.questionId}>
        {editMode ? (
          <HeaderEditMode />
        ) : (
          <Header
            onBack={onBack}
            currentStep={questionIndex + 1 + Number(!!showRaqOptions)}
            maxSteps={maxSteps}
            onCancel={() => {
              exitWithFeedbackCheck(onDismiss);
            }}
            disableDismiss={disableDismiss}
          />
        )}
        {showRaqOptions && <RAQOptions />}
        {question && !showRaqOptions && (
          <Component
            {...question}
            savedAnswer={savedAnswer}
            onNext={onNext}
            onSkip={onSkip}
            editMode={editMode}
          />
        )}
      </Fragment>
    </Modal>
  );
};

export default JobRefinementModal;
